export interface ParsedPromptResult {
  prompts: string[];
  isBatch: boolean;
}

export const parsePromptIntoCards = (prompt: string): ParsedPromptResult => {
  if (!prompt || prompt.trim().length === 0) {
    return { prompts: [prompt], isBatch: false };
  }

  console.log('[PromptParser] Input prompt:', prompt.substring(0, 200) + '...');

  // Check if this is a narration prompt (flowing story text)
  const isNarrationPrompt = isNarrationContent(prompt);
  
  if (isNarrationPrompt) {
    console.log('[PromptParser] Detected narration content - keeping as single prompt');
    return { prompts: [prompt], isBatch: false };
  }

  // Detection patterns for batch prompts
  const numberedPattern = /(?:^|\n)\s*\d+\.\s/gm;
  const imagePattern = /^Image\s+\d+[:.]?\s/gmi;
  const scenePattern = /^Scene\s+\d+[:.]?\s/gmi;
  const videoPattern = /^Video\s+\d+[:.]?\s/gmi;
  
  // Additional patterns for Claude responses
  const imageGenerationPattern = /\*\*IMAGE GENERATION PROMPTS:\*\*/i;
  const videoProductionPattern = /\*\*VIDEO PRODUCTION BRIEF:\*\*/i;

  // Check if it's a batch prompt
  const numberedMatches = prompt.match(numberedPattern);
  const imageMatches = prompt.match(imagePattern);
  const sceneMatches = prompt.match(scenePattern);
  const videoMatches = prompt.match(videoPattern);
  
  // Check for Claude-style formatted responses
  const hasImageGeneration = imageGenerationPattern.test(prompt);
  const hasVideoProduction = videoProductionPattern.test(prompt);

  console.log('[PromptParser] Pattern matches:', {
    numbered: numberedMatches?.length || 0,
    image: imageMatches?.length || 0,
    scene: sceneMatches?.length || 0,
    video: videoMatches?.length || 0,
    hasImageGeneration,
    hasVideoProduction
  });

  const totalMatches = (numberedMatches?.length || 0) + (imageMatches?.length || 0) + 
                      (sceneMatches?.length || 0) + (videoMatches?.length || 0);

  // If we have 2 or more matches OR Claude-style formatting, treat as batch
  if (totalMatches >= 2 || (hasImageGeneration && numberedMatches && numberedMatches.length >= 2)) {
    let splitPrompts = splitPromptByPatterns(prompt, {
      numberedMatches,
      imageMatches,
      sceneMatches,
      videoMatches,
      hasImageGeneration
    });

    if (splitPrompts.length >= 2) {
      return { prompts: splitPrompts, isBatch: true };
    }
  }

  // For non-narration content, still try fallback splitting but be more conservative
  if (prompt.length > 1500) { // Increased threshold
    const fallbackSplit = splitLongPrompt(prompt);
    if (fallbackSplit.length >= 2) {
      return { prompts: fallbackSplit, isBatch: true };
    }
  }

  console.log('[PromptParser] Treating as single prompt');
  return { prompts: [prompt], isBatch: false };
};

// Helper function to detect narration content
const isNarrationContent = (content: string): boolean => {
  const narrationIndicators = [
    // Story flow indicators
    'suddenly', 'then', 'as they', 'together', 'meanwhile',
    // Character actions in narrative form
    'the bunny', 'the turtle', 'she exclaimed', 'he replied', 'they walked',
    // Storytelling phrases
    'in a sunny', 'filled with', 'were enjoying', 'discovered that',
    // Dialogue integration
    'replied', 'said', 'called out', 'reassured', 'remembered'
  ];
  
  const contentLower = content.toLowerCase();
  const indicatorCount = narrationIndicators.filter(indicator => 
    contentLower.includes(indicator)
  ).length;
  
  // If it has multiple narration indicators and reads like a story, treat as narration
  return indicatorCount >= 3;
};

interface PromptPatterns {
  numberedMatches: RegExpMatchArray | null;
  imageMatches: RegExpMatchArray | null;
  sceneMatches: RegExpMatchArray | null;
  videoMatches: RegExpMatchArray | null;
  hasImageGeneration: boolean;
}

const splitPromptByPatterns = (prompt: string, patterns: PromptPatterns): string[] => {
  let splitPrompts: string[] = [];

  // Try different splitting approaches
  if (patterns.numberedMatches && patterns.numberedMatches.length >= 2) {
    console.log('[PromptParser] Using numbered splitting');
    
    // Special handling for Claude's formatted responses
    if (patterns.hasImageGeneration) {
      const cleanPrompt = prompt.replace(/\*\*IMAGE GENERATION PROMPTS:\*\*\s*/i, '');
      splitPrompts = cleanPrompt.split(/(?=(?:^|\n)\s*\d+\.\s)/gm);
    } else {
      splitPrompts = prompt.split(/(?=(?:^|\n)\s*\d+\.\s)/gm);
    }
  } else if (patterns.imageMatches && patterns.imageMatches.length >= 2) {
    console.log('[PromptParser] Using image splitting');
    splitPrompts = prompt.split(/(?=^Image\s+\d+[:.]?\s)/gmi);
  } else if (patterns.sceneMatches && patterns.sceneMatches.length >= 2) {
    console.log('[PromptParser] Using scene splitting');
    splitPrompts = prompt.split(/(?=^Scene\s+\d+[:.]?\s)/gmi);
  } else if (patterns.videoMatches && patterns.videoMatches.length >= 2) {
    console.log('[PromptParser] Using video splitting');
    splitPrompts = prompt.split(/(?=^Video\s+\d+[:.]?\s)/gmi);
  }

  // Clean up the split prompts
  splitPrompts = splitPrompts
    .filter(p => p.trim().length > 0)
    .map(p => p.trim())
    .filter(p => p.length > 10); // Filter out very short fragments

  console.log('[PromptParser] Split into', splitPrompts.length, 'prompts');
  if (splitPrompts.length > 0) {
    console.log('[PromptParser] First prompt preview:', splitPrompts[0]?.substring(0, 100));
  }

  return splitPrompts;
};

const splitLongPrompt = (prompt: string): string[] => {
  console.log('[PromptParser] Trying intelligent splitting for long prompt');
  
  // First try to split by natural breaks (double line breaks, sections)
  const sectionBreaks = prompt.split(/\n\n+/);
  if (sectionBreaks.length >= 2) {
    const filteredSections = sectionBreaks
      .map(section => section.trim())
      .filter(section => section.length > 100); // Only keep substantial sections
    
    if (filteredSections.length >= 2) {
      console.log('[PromptParser] Split by sections into', filteredSections.length, 'prompts');
      return filteredSections;
    }
  }
  
  // If no natural breaks, split by complete sentences but be more conservative
  const sentences = prompt.split(/(?<=[.!?])\s+(?=[A-Z])/);
  
  if (sentences.length >= 6) { // Only split if we have many sentences
    // Group sentences into larger logical prompts (3-4 sentences each)
    const groupedPrompts: string[] = [];
    for (let i = 0; i < sentences.length; i += 3) {
      const group = sentences.slice(i, i + 3).join(' ');
      if (group.trim().length > 150) { // Larger minimum size
        groupedPrompts.push(group.trim());
      }
    }
    
    if (groupedPrompts.length >= 2) {
      console.log('[PromptParser] Split by sentence groups into', groupedPrompts.length, 'prompts');
      return groupedPrompts;
    }
  }
  
  // If all else fails, keep as single prompt
  console.log('[PromptParser] Keeping as single prompt - no good split points found');
  return [prompt];
};
